import { PROJECT_FILES_TAG, PROJECT_TAG, PROJECTS_LIST_TAG } from '@/api/constants';
import { api } from '@/api/core';
import { getSavedChatArtefactsApi } from './getSavedChatArtefacts';
import { saveMockChatArtefact, shouldMockArtefactSaveFail } from './mockProjectFiles';
import { mockDelay, readMockProjectError } from './mockUtils';

/** Shape assumed until the backend contract exists */
export interface SaveChatArtefactToProjectRequest {
  projectId: string;
  chatId: string;
  chatTitle: string | null;
  draftVersionId: string;
  fileName: string;
}

export interface SaveChatArtefactToProjectResponse {
  fileId: string;
}

const MOCK_RESPONSE_DELAY_MS = 800;

const saveChatArtefactToProjectApi = api.injectEndpoints({
  endpoints: (builder) => ({
    saveChatArtefactToProject: builder.mutation<SaveChatArtefactToProjectResponse, SaveChatArtefactToProjectRequest>({
      // The backend endpoint does not exist yet: the draft joins the mock file list (see mockProjectFiles.ts).
      // Saving a draft again replaces its file with the draft's current content.
      queryFn: async (request) => {
        await mockDelay(MOCK_RESPONSE_DELAY_MS);
        const mockError = readMockProjectError();
        if (mockError) return { error: mockError };
        if (shouldMockArtefactSaveFail(request)) {
          return { error: { status: 500, data: 'The file could not be saved' } };
        }
        const file = saveMockChatArtefact(request);

        return file ? { data: { fileId: file.fileId } } : { error: { status: 404, data: 'Project not found' } };
      },
      // Counts the draft as saved straight away, so its button doesn't flip back to "Save" while the list refetches.
      async onQueryStarted({ projectId, draftVersionId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          getSavedChatArtefactsApi.util.updateQueryData('getSavedChatArtefacts', { projectId }, (draft) => {
            if (!draft.draftVersionIds.includes(draftVersionId)) draft.draftVersionIds.push(draftVersionId);
          }),
        );
      },
      invalidatesTags: (_result, error, { projectId }) =>
        error
          ? []
          : [
              { type: PROJECT_FILES_TAG, id: projectId },
              { type: PROJECT_TAG, id: projectId },
              { type: PROJECTS_LIST_TAG },
            ],
    }),
  }),
});

export const { useSaveChatArtefactToProjectMutation } = saveChatArtefactToProjectApi;
